import { isBoolean } from 'lodash-es'
import { ControlPriorities } from '../extras/ControlPriorities'
import { System } from './System'
import { NotificationQueue } from '../../client/notification-queue'
import { SessionShellState } from '../../client/session-shell-state'

const appPanes = ['app', 'script', 'nodes', 'meta']

export class ClientUI extends System {
  constructor(world) {
    super(world)
    this.state = {
      visible: true,
      active: false,
      app: null,
      pane: null,
      reticleSuppressors: 0,
    }
    this.lastAppPane = 'app'
    this.control = null
    this.notifications = new NotificationQueue({ onChange: state => this.world.emit('notifications', state) })
    this.connectionFailed = false
    this.previousParty = null
    this.previousQuests = null
    this.previousWallet = null
    this.sessionShell = new SessionShellState({ onChange: state => this.onSessionShellChange(state) })
    this.state.sessionShell = this.sessionShell.snapshot()
  }

  start() {
    this.control = this.world.controls.bind({ priority: ControlPriorities.CORE_UI })
    this.world.on('toast', this.onLegacyToast)
    this.world.on('connection', this.onConnectionNotice)
    this.world.on('screen:party', this.onPartyNotice)
    this.world.on('screen:quests', this.onQuestNotice)
    this.world.on('screen:wallet', this.onWalletNotice)
    this.world.on('connection', this.onShellConnection)
    this.sessionShell.setConnection(this.world.network?.connection?.snapshot?.())
    this.applyLocalInputOwnership(this.state.sessionShell.localInputPaused)
  }

  update() {
    if (this.notifications.snapshot().modal) return
    if (this.control.escape.pressed) {
      if (this.sessionShell.closeRoute()) return
      if (this.state.pane) {
        this.state.pane = null
        this.broadcast()
      } else if (this.state.app) {
        this.state.app = null
        this.broadcast()
      } else if (this.state.sessionShell.mode === 'pause') this.sessionShell.resume()
      else if (this.state.sessionShell.mode === 'world') this.sessionShell.pause()
    }
    if (
      this.control.keyZ.pressed &&
      !this.control.metaLeft.down &&
      !this.control.controlLeft.down &&
      !this.control.shiftLeft.down
    ) {
      this.state.visible = !this.state.visible
      this.broadcast()
    }
    if (this.control.pointer.locked && this.state.active) {
      this.state.active = false
      this.broadcast()
    }
    if (!this.control.pointer.locked && !this.state.active) {
      this.state.active = true
      this.broadcast()
    }
  }

  togglePane(pane) {
    if (pane === null || this.state.pane === pane) {
      this.state.pane = null
    } else {
      // if (appPanes.includes(this.state.pane) && !appPanes.includes(pane)) {
      //   this.state.app = null
      // }
      this.state.pane = pane
      if (appPanes.includes(pane)) {
        this.lastAppPane = pane
      }
    }
    this.broadcast()
  }

  toggleVisible(value) {
    value = isBoolean(value) ? value : !this.state.visible
    if (this.state.visible === value) return
    this.state.visible = value
    this.broadcast()
  }

  setApp(app) {
    this.state.app = app
    this.state.pane = app ? this.lastAppPane : null
    this.broadcast()
  }

  suppressReticle() {
    this.state.reticleSuppressors++
    let released
    this.broadcast()
    return () => {
      if (released) return
      this.state.reticleSuppressors--
      this.broadcast()
      released = true
    }
  }

  confirm(options) {
    return this.notifications.confirm(options)
  }

  onShellConnection = state => this.sessionShell.setConnection(state)

  onSessionShellChange(state) {
    const changed = this.state.sessionShell?.localInputPaused !== state.localInputPaused
    this.state.sessionShell = state
    if (changed && this.control) this.applyLocalInputOwnership(state.localInputPaused)
    this.broadcast()
  }

  applyLocalInputOwnership(blocked) {
    const buttons = ['keyW', 'keyA', 'keyS', 'keyD', 'keyC', 'keyE', 'keyF', 'keyQ', 'keyR', 'arrowUp', 'arrowDown', 'arrowLeft', 'arrowRight', 'space', 'shiftLeft', 'shiftRight', 'mouseLeft', 'mouseRight', 'touchA', 'xrLeftTrigger', 'xrRightTrigger', 'xrLeftBtn1', 'xrRightBtn1']
    for (const key of buttons) this.control[key].capture = blocked
    this.control.xrLeftStick.capture = blocked
    this.control.xrRightStick.capture = blocked
    this.control.touchStick.capture = blocked
    this.control.scrollDelta.capture = blocked
    if (blocked) {
      this.world.controls.releaseAllButtons?.()
      this.world.controls.unlockPointer?.()
    }
  }

  enterWorld() { return this.sessionShell.enter() }
  pauseWorld() { return this.sessionShell.pause() }
  resumeWorld() { return this.sessionShell.resume() }
  openSessionRoute(route) { return this.sessionShell.open(route) }
  closeSessionRoute() { return this.sessionShell.closeRoute() }
  startSessionAction(kind) { return this.sessionShell.startAction(kind) }
  finishSessionAction(kind, options) { return this.sessionShell.finishAction(kind, options) }

  notify(options) {
    return this.notifications.enqueue(typeof options === 'string' ? { message: options } : options)
  }

  onLegacyToast = value => this.notify(typeof value === 'string' ? { message: value, dedupeKey: `legacy:${value}` } : value)
  onConnectionNotice = state => {
    if (state?.status === 'error') {
      this.connectionFailed = true
      this.notify({ kind: 'connection', tone: 'warning', title: 'СВЯЗЬ ПРЕРВАНА', message: state.error?.message || 'Клиент готовит повторное подключение.', dedupeKey: `connection:error:${state.error?.code || 'unknown'}` })
    }
    if (state?.status === 'reconnecting') this.notifications.retainDurable()
    if (state?.status === 'ready' && this.connectionFailed) {
      this.connectionFailed = false
      this.notify({ kind: 'connection', tone: 'success', title: 'СВЯЗЬ ВОССТАНОВЛЕНА', message: 'Сессия и персонаж снова синхронизированы с миром.', dedupeKey: 'connection:restored' })
    }
  }
  onPartyNotice = state => {
    const current = new Set((state?.invitations || []).map(invite => String(invite.id)))
    for (const invite of state?.invitations || []) {
      if (!this.previousParty?.has(String(invite.id))) this.notify({ kind: 'invite', title: 'ПРИГЛАШЕНИЕ В ГРУППУ', message: `${invite.fromName} приглашает в «${invite.groupName}».`, dedupeKey: `party:${invite.id}`, expiresAt: Number(invite.expiresAt) })
    }
    this.previousParty = current
  }
  onQuestNotice = quests => {
    const current = new Map((quests || []).map(quest => [String(quest.id), quest]))
    if (this.previousQuests) for (const [id, quest] of current) {
      const before = this.previousQuests.get(id)
      if (quest.claimed && before && !before.claimed) this.notify({ kind: 'reward', title: 'НАГРАДА ПОЛУЧЕНА', message: quest.name || 'Задание завершено.', dedupeKey: `quest:${id}:claimed`, durable: true })
    }
    this.previousQuests = current
  }
  onWalletNotice = wallet => {
    const current = new Map((wallet?.plots || []).map(plot => [String(plot.id), plot]))
    if (this.previousWallet) for (const [id, plot] of current) {
      const before = this.previousWallet.get(id)
      if (plot.mine && before && !before.mine) this.notify({ kind: 'purchase', tone: 'success', title: 'УЧАСТОК ПОДТВЕРЖДЁН', message: `Участок #${plot.index} записан за персонажем сервером мира.`, dedupeKey: `plot:${id}:owned`, durable: true })
    }
    this.previousWallet = current
  }

  broadcast() {
    this.world.emit('ui', { ...this.state })
  }

  destroy() {
    this.world.off('toast', this.onLegacyToast)
    this.world.off('connection', this.onConnectionNotice)
    this.world.off('screen:party', this.onPartyNotice)
    this.world.off('screen:quests', this.onQuestNotice)
    this.world.off('screen:wallet', this.onWalletNotice)
    this.world.off('connection', this.onShellConnection)
    this.notifications.destroy()
    this.control?.release()
    this.control = null
  }
}
